import { QueryClientProvider } from '@tanstack/react-query';
import { ConfirmProvider, createQueryClient, EmptyState, FullPageSpinner, ToastProvider } from '@wl/ui';
import { lazy, type ReactNode, Suspense } from 'react';
import { createBrowserRouter, Link, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import { allowed } from '../lib/access';
import { AuthProvider, useAuth, useMe } from '../lib/auth';
import { BrandingProvider, useSystemStatus } from '../lib/branding';
import { AppLayout } from './AppLayout';
import { type AppModule, MODULES } from './modules';

const queryClient = createQueryClient();

const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));
const InvitePage = lazy(() => import('../pages/auth/InvitePage'));
const VerifyEmailPage = lazy(() => import('../pages/auth/VerifyEmailPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const HandoffPage = lazy(() => import('../pages/auth/HandoffPage'));
const MfaSetupRequiredPage = lazy(() => import('../pages/auth/MfaSetupRequiredPage'));
const MaintenancePage = lazy(() => import('../pages/auth/MaintenancePage'));

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<FullPageSpinner />}>{children}</Suspense>;
}

/** Oturum yoksa girişe yönlendirir; 2FA zorunluysa kurulum ekranını gösterir. */
function RequireAuth() {
  const { status, me } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous' || !me) {
    const next = location.pathname + location.search;
    return <Navigate to={`/auth/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
  }
  if (me.mfa.setupRequired) {
    return (
      <Lazy>
        <MfaSetupRequiredPage />
      </Lazy>
    );
  }
  return <AppLayout />;
}

/**
 * Bakımda giriş, kayıt ve uygulama ekranları yerine tam sayfa bakım ekranı.
 * Platform yöneticisinin kullanıcı olarak girişi (impersonation) etkilenmez.
 */
function MaintenanceGate() {
  const system = useSystemStatus();
  const { me } = useAuth();
  if (system.data?.maintenance && me?.impersonation?.actor.type !== 'platform_admin') {
    return (
      <Lazy>
        <MaintenancePage />
      </Lazy>
    );
  }
  return <Outlet />;
}

/** Girişliyken giriş sayfalarına gelinirse uygulamaya döner. */
function GuestOnly() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'authenticated') {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={next?.startsWith('/') && !next.startsWith('//') ? next : '/'} replace />;
  }
  return (
    <Lazy>
      <Outlet />
    </Lazy>
  );
}

const PAGES = new Map(MODULES.map((m) => [m, lazy(m.load)]));

function Guarded({ module }: { module: AppModule }) {
  const me = useMe();
  const Page = PAGES.get(module)!;
  if (!allowed(me, module)) return <NotFound />;
  return (
    <Suspense fallback={<div className="py-10"><FullPageSpinnerInline /></div>}>
      <Page />
    </Suspense>
  );
}

function FullPageSpinnerInline() {
  return (
    <div className="flex justify-center text-muted">
      <svg className="animate-spin" width={24} height={24} viewBox="0 0 24 24" fill="none" role="status" aria-label="Yükleniyor">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
        <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export function NotFound() {
  return (
    <EmptyState
      icon="alert"
      title="Sayfa bulunamadı"
      description="Aradığınız sayfa yok ya da erişiminize kapalı."
      action={
        <Link to="/" className="text-sm font-medium text-primary underline">
          Ana sayfaya dön
        </Link>
      }
    />
  );
}

function Root() {
  return (
    <AuthProvider>
      <BrandingProvider>
        <ToastProvider>
          <ConfirmProvider>
            <Outlet />
          </ConfirmProvider>
        </ToastProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      { path: '/auth/handoff', element: <Lazy><HandoffPage /></Lazy> },
      {
        element: <MaintenanceGate />,
        children: [
          {
            path: '/auth',
            element: <GuestOnly />,
            children: [
              { path: 'login', element: <LoginPage /> },
              { path: 'forgot-password', element: <ForgotPasswordPage /> },
              { path: 'register', element: <RegisterPage /> },
            ],
          },
          // Bağlantıyla gelinen sayfalar oturum açıkken de çalışır (ör. başka hesabın daveti).
          { path: '/auth/reset-password', element: <Lazy><ResetPasswordPage /></Lazy> },
          { path: '/auth/invite', element: <Lazy><InvitePage /></Lazy> },
          { path: '/auth/verify-email', element: <Lazy><VerifyEmailPage /></Lazy> },
          {
            element: <RequireAuth />,
            children: [...MODULES.map((m) => ({ path: m.path, element: <Guarded module={m} /> })), { path: '*', element: <NotFound /> }],
          },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
